import { constantTimeEquals } from '@/lib/constant-time';

import { API_CONFIG, API_ENDPOINTS, HTTP_METHODS } from './config';
import { resolveApiUrl } from './resolve-url';
import { ApiResponse, PaginatedResponse } from './types';

// Request configuration interface
interface RequestConfig {
  method: keyof typeof HTTP_METHODS;
  url: string;
  data?: any;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
}

// API Client Class
class ApiClient {
  private defaultHeaders: Record<string, string>;
  private timeout: number;
  private uploadTimeout: number;
  private permissionsHash: string | null = null;
  private tokenKey = 'auth_token';
  private isRefreshing = false;
  private refreshQueue: Array<{
    resolve: (token: string) => void;
    reject: (err: Error) => void;
  }> = [];

  constructor() {
    this.timeout = API_CONFIG.timeout;
    this.uploadTimeout = API_CONFIG.uploadTimeout;
    this.defaultHeaders = {
      'Content-Type': 'application/json'
    };
  }

  private processPermissionsHash(hash: string | null): void {
    if (!hash) return;

    const previous = this.permissionsHash;
    this.permissionsHash = hash;

    // Compared in constant time: the digest is a server-issued fingerprint of
    // the caller's permission set, and a comparison that short-circuits on the
    // first differing character tells anyone who can time it how much of a
    // digest they have guessed right (CWE-208).
    if (previous === null || constantTimeEquals(hash, previous)) return;

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('permissions-changed'));
    }
  }

  private checkPermissionsHash(headers: Headers): void {
    this.processPermissionsHash(headers.get('X-Permissions-Hash'));
  }

  /**
   * Clears the stored hash so the next response re-baselines silently instead
   * of firing a redundant `permissions-changed` event. Called by the Reverb
   * `.permissions-changed` handler, which already triggers the refresh itself.
   */
  resetPermissionsHash(): void {
    this.permissionsHash = null;
  }

  setTokenKey(key: string) {
    this.tokenKey = key;
  }

  setAuthToken(token: string) {
    this.defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  removeAuthToken() {
    delete this.defaultHeaders['Authorization'];
  }

  setLocale(locale: string) {
    this.defaultHeaders['Accept-Language'] = locale;
  }

  private getToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(this.tokenKey);
    }
    return null;
  }

  private async attemptTokenRefresh(): Promise<string> {
    if (this.isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        this.refreshQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const token = this.getToken();
      if (!token) throw new Error('No token to refresh');

      const isPortal = this.tokenKey !== 'auth_token';
      const refreshEndpoint = isPortal
        ? API_ENDPOINTS.PORTAL_AUTH.REFRESH
        : API_ENDPOINTS.AUTH.REFRESH;
      // gitlab-advanced-sast-exclude -- resolveApiUrl() pins every request to the configured API origin and throws on anything else.
      const res = await fetch(resolveApiUrl(refreshEndpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
          'Accept-Language': this.defaultHeaders['Accept-Language'] || 'en'
        },
        credentials: 'omit'
      });

      if (!res.ok) throw new Error('Refresh failed');

      const body = await res.json();
      const newToken: string = body.data?.token ?? body.token;
      if (!newToken) throw new Error('No token in refresh response');

      // Persist the new token
      if (typeof window !== 'undefined') {
        localStorage.setItem(this.tokenKey, newToken);
      }
      this.setAuthToken(newToken);

      // Resolve queued requests
      this.refreshQueue.forEach((q) => q.resolve(newToken));
      return newToken;
    } catch (err) {
      this.refreshQueue.forEach((q) =>
        q.reject(err instanceof Error ? err : new Error('Refresh failed'))
      );

      // Session is truly expired — notify auth context
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('session-expired'));
      }

      throw err;
    } finally {
      this.isRefreshing = false;
      this.refreshQueue = [];
    }
  }

  private buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((item) =>
            searchParams.append(`${key}[]`, item.toString())
          );
        } else {
          searchParams.append(key, value.toString());
        }
      }
    });

    return searchParams.toString();
  }

  private async request<T>(config: RequestConfig): Promise<ApiResponse<T>> {
    const { method, url, data, params, headers = {} } = config;

    const token = this.getToken();
    if (token) {
      this.setAuthToken(token);
    }

    let path = url;
    if (params) {
      const queryString = this.buildQueryString(params);
      if (queryString) {
        path += `?${queryString}`;
      }
    }

    const requestOptions: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: this.defaultHeaders['Authorization'] || '',
        'Accept-Language': this.defaultHeaders['Accept-Language'] || 'en',
        ...headers
      },
      signal:
        config.signal ||
        (config.timeout || this.timeout
          ? AbortSignal.timeout(config.timeout ?? this.timeout)
          : undefined),
      redirect: 'follow',
      mode: 'cors',
      credentials: 'omit'
    };

    if (data && method !== HTTP_METHODS.GET) {
      requestOptions.body = JSON.stringify(data);
    }

    try {
      // gitlab-advanced-sast-exclude -- resolveApiUrl() pins every request to the configured API origin and throws on anything else.
      const response = await fetch(resolveApiUrl(path), requestOptions);

      this.checkPermissionsHash(response.headers);

      if (!response.ok) {
        // Attempt token refresh on 401 (skip if this IS the refresh request)
        const isRefreshRequest =
          url === API_ENDPOINTS.AUTH.REFRESH ||
          url === API_ENDPOINTS.PORTAL_AUTH.REFRESH;

        if (response.status === 401 && !isRefreshRequest && this.getToken()) {
          try {
            await this.attemptTokenRefresh();
            return this.request<T>(config);
          } catch {
            // Refresh failed — fall through to throw the original 401
          }
        }

        let finalMessage = 'Request failed';
        let errorCode: string | undefined;
        let errorData: any;

        try {
          errorData = await response.json();

          const messages: string[] = [];

          if (typeof errorData?.message === 'string') {
            messages.push(errorData.message);
          }

          if (
            typeof errorData?.error === 'string' &&
            errorData.error !== errorData.message
          ) {
            messages.push(errorData.error);
          }

          if (
            errorData?.errors &&
            typeof errorData.errors === 'object' &&
            !Array.isArray(errorData.errors)
          ) {
            const flatErrors = Object.values(errorData.errors).flat();
            flatErrors.forEach((e) => {
              if (typeof e === 'string') messages.push(e);
            });
          }

          if (Array.isArray(errorData?.errors)) {
            errorData.errors.forEach((e: any) => {
              if (typeof e === 'string') messages.push(e);
            });
          }

          if (messages.length > 0) {
            finalMessage = messages.join('\n');
          }

          errorCode = errorData?.code;
        } catch {
          finalMessage = response.statusText || finalMessage;
        }

        if (
          response.status === 403 &&
          errorCode === 'LICENSE_EXPIRED' &&
          typeof window !== 'undefined'
        ) {
          window.dispatchEvent(new CustomEvent('license-expired'));
        }

        throw new ApiError(finalMessage, response.status, {
          code: errorCode,
          data: errorData
        });
      }

      let responseData;
      const responseClone = response.clone();
      try {
        responseData = await response.json();
      } catch {
        const responseText = await responseClone.text();
        console.error('Failed to parse JSON response. Body:', responseText);
        throw new ApiError(
          'Invalid JSON response from server',
          response.status
        );
      }

      return {
        ...responseData,
        success: true
      };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timeout', 408);
        }
        throw new ApiError(error.message, 0);
      }

      throw new ApiError('Unknown error occurred', 0);
    }
  }

  async get<T>(
    url: string,
    params?: Record<string, any>,
    signal?: AbortSignal
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: HTTP_METHODS.GET,
      url,
      params,
      signal
    });
  }

  async post<T>(
    url: string,
    data?: any,
    config?: Pick<RequestConfig, 'signal' | 'timeout'>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: HTTP_METHODS.POST,
      url,
      data,
      ...config
    });
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: HTTP_METHODS.PUT,
      url,
      data
    });
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: HTTP_METHODS.PATCH,
      url,
      data
    });
  }

  async delete<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>({
      method: HTTP_METHODS.DELETE,
      url,
      data
    });
  }

  async getPaginated<T>(
    url: string,
    params: Record<string, any> = {}
  ): Promise<PaginatedResponse<T>> {
    const queryParams = { ...params };

    // Map sorting array to sort_by + sort_direction query params
    if (Array.isArray(queryParams.sorting) && queryParams.sorting.length > 0) {
      const sort = queryParams.sorting[0];
      queryParams.sort_by = sort.by;
      queryParams.sort_direction = sort.as;
      delete queryParams.sorting;
    } else {
      delete queryParams.sorting;
    }

    const response = await this.get<PaginatedResponse<T>>(url, queryParams);
    return response as unknown as PaginatedResponse<T>;
  }

  /**
   * POST a multipart body. Uses `API_CONFIG.uploadTimeout` (not the much
   * shorter JSON `timeout`) because a single chunk can be several MB and the
   * server persists every file before it answers.
   */
  async uploadFormData<T>(
    url: string,
    formData: FormData,
    onProgress?: (progress: number) => void,
    timeout?: number
  ): Promise<ApiResponse<T>> {
    const token = this.getToken();

    if (token) {
      this.setAuthToken(token);
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        });
      }

      xhr.addEventListener('load', () => {
        this.processPermissionsHash(
          xhr.getResponseHeader('X-Permissions-Hash')
        );

        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch {
            reject(new Error('Invalid JSON response'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            const messages: string[] = [];
            if (typeof errorData?.message === 'string')
              messages.push(errorData.message);
            if (typeof errorData?.error === 'string')
              messages.push(errorData.error);
            if (
              errorData?.errors &&
              typeof errorData.errors === 'object' &&
              !Array.isArray(errorData.errors)
            ) {
              const flatErrors = Object.values(errorData.errors).flat();
              flatErrors.forEach((e) => {
                if (typeof e === 'string') messages.push(e);
              });
            }
            if (Array.isArray(errorData?.errors)) {
              (errorData.errors as unknown[]).forEach((e) => {
                if (typeof e === 'string') messages.push(e);
              });
            }
            const finalMessage =
              messages.length > 0 ? messages.join('\n') : `HTTP ${xhr.status}`;
            if (
              xhr.status === 403 &&
              errorData?.code === 'LICENSE_EXPIRED' &&
              typeof window !== 'undefined'
            ) {
              window.dispatchEvent(new CustomEvent('license-expired'));
            }
            reject(
              new ApiError(finalMessage, xhr.status, {
                code: errorData?.code,
                data: errorData
              })
            );
          } catch {
            reject(
              new ApiError(`HTTP ${xhr.status}: ${xhr.statusText}`, xhr.status)
            );
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error occurred'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('Request timeout'));
      });

      xhr.open('POST', resolveApiUrl(url));
      xhr.setRequestHeader(
        'Authorization',
        this.defaultHeaders['Authorization'] || ''
      );
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader(
        'Accept-Language',
        this.defaultHeaders['Accept-Language'] || 'en'
      );
      xhr.timeout = timeout ?? this.uploadTimeout;

      xhr.send(formData);
    });
  }
}

// Custom Error Class
export class ApiError extends Error {
  status: number;
  code?: string;
  data?: unknown;
  isApiError = true;

  constructor(
    message: string,
    status: number,
    opts?: { code?: string; data?: unknown; cause?: unknown }
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = opts?.code;
    this.data = opts?.data;
    if (opts?.cause) (this as any).cause = opts.cause;
  }
}

export type ApiErrorType = ApiError;

export function isApiError(err: unknown): err is ApiError {
  return (
    !!err &&
    typeof err === 'object' &&
    (err as any).isApiError === true &&
    typeof (err as any).status === 'number'
  );
}

export const apiClient = new ApiClient();
export default apiClient;
